import { calendarIcon, closeIcon, saveIcon } from "assets/images";
import { Modal, Typography, IconButton, Grid, Button } from "@mui/material";
import { Box } from "@mui/system";
import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import {
  DUE_DATE_UPDATE_SUCCESS_MSG,
  ENTER_VALID_DATE_MSG,
  PAST_DATE_ERROR_MSG,
} from "utils/constants/Messages";
import { DATE_PICKER_FORMAT, DATE_ONLY_FORMAT } from "utils/constants/constant";
import { IDeferDetails } from "utils/interfaces/student-profile-details";
import dayjs from "dayjs";
import { Controller, useForm } from "react-hook-form";
import {
  DeferSLPPayload,
  DeferSLPSuccessPayload,
} from "store/studentProfileDetails/types";
import {
  hideLoaderForBatchApiCall,
  showLoaderForBatchApiCall,
} from "utils/helper";
import { toast } from "react-toastify";
import { useEffect } from "react";

function CalendarIcon() {
  return (
    <div className="calendar-icon">
      <img src={calendarIcon} alt="calendar" />
    </div>
  );
}

interface IDeferSLPModalProps {
  openDeferSLP: boolean;
  dueDate: string;
  slpId: number;
  deferSLPRequest: (payload: DeferSLPPayload) => void;
  setOpenDeferSLP: React.Dispatch<React.SetStateAction<boolean>>;
  deferSLPCallback: (dueDate: DeferSLPSuccessPayload) => void;
}

const DeferSLPModal: React.FC<IDeferSLPModalProps> = (props) => {
  const {
    openDeferSLP,
    dueDate,
    slpId,
    deferSLPRequest,
    setOpenDeferSLP,
    deferSLPCallback,
  } = props;

  const {
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm<IDeferDetails>({
    defaultValues: {
      dueDate,
    },
  });

  const onDeferSLPSuccess = (response: DeferSLPSuccessPayload) => {
    reset({ dueDate: response?.deferSLPDetails.dueDate ?? "" });
    deferSLPCallback(response);
    hideLoaderForBatchApiCall();
    toast.success(DUE_DATE_UPDATE_SUCCESS_MSG);
  };

  const onSubmitDeferDate = async (data: IDeferDetails) => {
    if (slpId && deferSLPRequest) {
      showLoaderForBatchApiCall();
      const payload = {
        values: {
          slpId,
          dueDate: data.dueDate,
        },
        callback: onDeferSLPSuccess,
      };
      deferSLPRequest(payload);
    }
    setOpenDeferSLP(false);
  };

  const handleDeferSLPClose = () => {
    setOpenDeferSLP(false);
    reset({ dueDate: dueDate ?? "" });
  };

  useEffect(() => {
    if (dueDate) {
      reset({ dueDate });
    }
  }, [dueDate, reset]);

  return (
    <Modal open={openDeferSLP} onClose={handleDeferSLPClose}>
      <Box className="common-modal modal-400">
        <Box className="modal-header">
          <Typography variant="h4">Defer SLP</Typography>
          <IconButton onClick={handleDeferSLPClose}>
            <img src={closeIcon} alt="close" />
          </IconButton>
        </Box>
        <form>
          <Box className="modal-body">
            <Grid container columnSpacing="16px">
              <Grid item xs>
                <Box sx={{ mb: 3 }} className="datepicker-box">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={["DesktopDatePicker"]}>
                      <Controller
                        control={control}
                        name="dueDate"
                        rules={{
                          required: true,
                          validate: (value) => {
                            const date = dayjs(value);
                            if (!date.isValid()) {
                              return ENTER_VALID_DATE_MSG;
                            }

                            if (date.isBefore(dayjs(), "day")) {
                              return PAST_DATE_ERROR_MSG;
                            }

                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <DesktopDatePicker
                            {...field}
                            className="date-picker defer-date-picker"
                            format={DATE_PICKER_FORMAT}
                            label={
                              <>
                                Due Date <span className="color-red">*</span>
                              </>
                            }
                            value={
                              watch("dueDate")
                                ? dayjs(watch("dueDate"))
                                : dayjs(new Date())
                            }
                            onChange={(date) => {
                              field.onChange(
                                date ? date.format(DATE_ONLY_FORMAT) : ""
                              );
                            }}
                            slots={{
                              openPickerIcon: CalendarIcon,
                            }}
                            minDate={dayjs()}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.dueDate,
                                helperText: errors.dueDate?.message || "",
                              },
                            }}
                          />
                        )}
                      />
                    </DemoContainer>
                  </LocalizationProvider>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Box className="modal-footer">
            <Button
              variant="contained"
              className="btn-save"
              onClick={handleSubmit(onSubmitDeferDate)}
            >
              <img src={saveIcon} alt="save" />
              Save
            </Button>
            <Button
              variant="outlined"
              className="btn-cancel"
              onClick={handleDeferSLPClose}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default DeferSLPModal;
